import PouchDB from 'pouchdb-browser';
import { clearAll, getDecoded, getJwt, refresh } from '@/lib/auth';
import { resolveConflicts } from '@/lib/conflicts';

const LOCAL_DB_NAME = 'ft-local';

let localDb: PouchDB.Database | null = null;
let remoteDb: PouchDB.Database | null = null;
interface ActiveSync {
  cancel: () => void;
}
let activeSync: ActiveSync | null = null;

function couchBase(): string {
  const fromEnv = import.meta.env.VITE_COUCH_URL as string | undefined;
  if (fromEnv) return fromEnv.replace(/\/data$/, '');
  return '/couch';
}

function remoteUrl(): string | null {
  const decoded = getDecoded();
  const tenantId = decoded?.tenant_id;
  if (!tenantId) return null;
  return `${couchBase()}/data_${tenantId}`;
}

export function getPouchDB(): PouchDB.Database {
  if (!localDb) {
    localDb = new PouchDB(LOCAL_DB_NAME);
  }
  return localDb;
}

const authedFetch: typeof fetch = async (input, init) => {
  const headers = new Headers(init?.headers ?? {});
  const jwt = getJwt();
  if (jwt && !headers.has('authorization')) {
    headers.set('authorization', `Bearer ${jwt}`);
  }
  const doIt = () =>
    PouchDB.fetch(input as RequestInfo, { ...(init ?? {}), headers });
  let res = await doIt();
  if (res.status === 401) {
    try {
      const newJwt = await refresh();
      headers.set('authorization', `Bearer ${newJwt}`);
      res = await PouchDB.fetch(input as RequestInfo, {
        ...(init ?? {}),
        headers,
      });
    } catch {
      await clearAll();
    }
  }
  return res;
};

export function getRemoteDB(): PouchDB.Database | null {
  if (!remoteDb) {
    const url = remoteUrl();
    if (!url) return null;
    remoteDb = new PouchDB(url, {
      fetch: authedFetch,
      skip_setup: true,
    } as PouchDB.Configuration.RemoteDatabaseConfiguration);
  }
  return remoteDb;
}

export interface SyncHandle {
  stop: () => void;
}

// Injected by the store so this module stays free of store imports. Called when
// the server rejects a replicated doc (validate_doc_update `denied`) — a write
// that violated a role rule or a conflict that never reached the server.
let replicationErrorHandler: ((info: unknown) => void) | null = null;
export function setReplicationErrorHandler(h: (info: unknown) => void): void {
  replicationErrorHandler = h;
}

// Injected by the store to drive the `online` flag from real replication state
// (active = reaching the server, paused-with-error = cannot reach it).
let onlineHandler: ((online: boolean) => void) | null = null;
export function setOnlineHandler(h: (online: boolean) => void): void {
  onlineHandler = h;
}

export function startSync(): SyncHandle {
  if (activeSync) {
    return { stop: () => stopSync() };
  }
  const local = getPouchDB();
  const remote = getRemoteDB();
  if (!remote) {
    return { stop: () => stopSync() };
  }

  // Per-tenant DB: no filter needed — _security on data_<tenant> enforces
  // read isolation natively. Both pull and push are straight live + retry.
  const pull = local.replicate.from(remote, { live: true, retry: true });
  const push = local.replicate.to(remote, { live: true, retry: true });

  // `denied` = a doc the server refused (validate_doc_update / auth) — surface
  // it. `error` is mostly transient connectivity that retry already handles, so
  // it is only logged to avoid toast spam while offline.
  const onDenied = (info: unknown): void => {
    console.error('[sync] replication denied:', info);
    if (replicationErrorHandler) replicationErrorHandler(info);
  };
  const onError = (err: unknown): void => {
    console.error('[sync] replication error:', err);
  };
  const markOnline = (v: boolean): void => {
    if (onlineHandler) onlineHandler(v);
  };

  // Converge conflicted docs shortly after a pull settles. Debounced so a burst
  // of incoming changes triggers a single pass.
  let resolveTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleResolve = (): void => {
    if (resolveTimer) return;
    resolveTimer = setTimeout(() => {
      resolveTimer = null;
      void resolveConflicts(local).catch(err =>
        console.error('[sync] conflict resolve failed:', err)
      );
    }, 500);
  };

  pull
    .on('denied', onDenied)
    .on('error', onError)
    .on('change', scheduleResolve)
    .on('active', () => markOnline(true))
    .on('paused', (err: unknown) => markOnline(!err));
  push
    .on('denied', onDenied)
    .on('error', onError)
    .on('active', () => markOnline(true))
    .on('paused', (err: unknown) => markOnline(!err));

  activeSync = {
    cancel: () => {
      try {
        pull.cancel();
      } catch {
        /* ignore */
      }
      try {
        push.cancel();
      } catch {
        /* ignore */
      }
    },
  };
  return { stop: () => stopSync() };
}

function stopSync(): void {
  if (activeSync) {
    try {
      activeSync.cancel();
    } catch {
      // ignore
    }
    activeSync = null;
  }
}

export async function destroyLocal(): Promise<void> {
  stopSync();
  if (localDb) {
    try {
      await localDb.destroy();
    } catch {
      // ignore
    }
    localDb = null;
  }
  remoteDb = null;
}
